import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Clock, Route, Save, Loader2, Wrench } from "lucide-react";
import api, { apiError } from "../lib/api";
import { BrandMark } from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { Eyebrow, inputCls, SignaturePad, MachineCard, useProTypeMap, withChecklists, CHECKLIST_422 } from "./NewReport";
import { missingRequired } from "../components/ProChecklist";

export default function EditReport() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useLang();
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [f, setF] = useState({ customer_name: "", address: "", contact_person: "", call_id: "", report_date: "", work_report: "", recipient_id: "" });
  const [reportLang, setReportLang] = useState("en");
  const [sig, setSig] = useState("");
  const [sigName, setSigName] = useState("");
  const [machines, setMachines] = useState([]);
  const [hours, setHours] = useState([]);
  const [trips, setTrips] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [machineTypes, setMachineTypes] = useState([]);
  const [status, setStatus] = useState("");
  const typeMap = useProTypeMap(user);

  useEffect(() => { api.get("/parts").then(({ data }) => setCatalog(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/machine-types").then(({ data }) => setMachineTypes(data)).catch(() => {}); }, []);

  useEffect(() => {
    Promise.all([api.get(`/reports/${id}`), api.get("/recipients")]).then(([r, rec]) => {
      const d = r.data;
      setF({ customer_name: d.customer_name || "", address: d.address || "", contact_person: d.contact_person || "", call_id: d.call_id || "", report_date: d.report_date || "", work_report: d.work_report || "", recipient_id: d.recipient_id || rec.data[0]?.id || "" });
      setSig(d.signature?.image || "");
      setSigName(d.signature?.name || "");
      setReportLang(d.language === "nl" ? "nl" : "en");
      setStatus(d.status || "");
      setMachines((d.machines || [{ serial_number: "", parts: [] }]).map((m) => ({ serial_number: m.serial_number || "", machine_type_id: m.machine_type_id || null, parts: m.parts || [], checklist: (m.checklist || []).map((c) => ({ item_id: c.item_id, done: !!c.done, note: c.note || "", photo_id: c.photo_id || null, photo_url: c.photo_url || null })) })));
      setHours(d.hours || []);
      setTrips(d.trips || []);
      setRecipients(rec.data);
      setReady(true);
    }).catch(() => { toast.error(t("Could not load report")); nav("/reports"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, nav]);

  if (user?.role === "office") { nav(`/reports/${id}`); return null; }
  if (!ready) return <div className="min-h-screen bg-[#0A0A0A] text-zinc-500 flex items-center justify-center">{t("Loading...")}</div>;

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setMachine = (i, k, v) => setMachines(machines.map((m, j) => j === i ? { ...m, [k]: v } : m));
  const addPart = (i, p) => setMachines(machines.map((m, j) => j === i ? { ...m, parts: [...m.parts, p] } : m));
  const removePart = (i, pi) => setMachines(machines.map((m, j) => j === i ? { ...m, parts: m.parts.filter((_, k) => k !== pi) } : m));
  const today = new Date().toISOString().slice(0, 10);

  const save = async () => {
    if (!f.customer_name.trim()) return toast.error(t("Customer name is required"));
    if (!machines.some((m) => (m.parts || []).length > 0)) return toast.error(t("Add at least one part to a machine"));
    if (status !== "draft") {
      const miss = missingRequired(machines, typeMap);
      if (miss.length) return toast.error(`${t(CHECKLIST_422)} ${t("Machine")} ${miss.join(", ")}`);
    }
    setLoading(true);
    const body = { ...f, machines: withChecklists(machines, typeMap), hours, trips, language: reportLang };
    const custName = sigName.trim() || f.contact_person || f.customer_name;
    if (sig || custName) body.signature = { image: sig, name: custName, date: f.report_date };
    try { await api.put(`/reports/${id}`, body); toast.success(t("Report saved")); nav(`/reports/${id}`); }
    catch (err) { toast.error(err.response?.status === 422 ? t(CHECKLIST_422) : apiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] max-w-md mx-auto sm:max-w-2xl md:max-w-4xl border-x border-zinc-800/40 pb-10">
      <div className="sticky top-0 z-40 bg-[#0A0A0A]/90 backdrop-blur-md px-4 py-4 border-b border-zinc-800/70 flex items-center justify-between">
        <button onClick={() => nav(`/reports/${id}`)} className="flex items-center gap-2 text-zinc-300" data-testid="edit-back-btn"><ArrowLeft size={20} /> {t("Back")}</button>
        <BrandMark />
      </div>
      <div className="px-4">
        <h1 className="text-2xl font-extrabold text-white mt-4">{t("Edit Report")}</h1>
        <p className="text-zinc-400 text-sm">{t("Saving updates the report without re-emailing. Use Resend to email again.")}</p>

        <div className="flex items-center justify-between mt-4 bg-[#18181B] border border-zinc-800 rounded-xl px-4 py-3" data-testid="report-lang-row">
          <span className="text-sm text-zinc-300">{t("Report language")}</span>
          <div className="inline-flex rounded-lg border border-zinc-700 overflow-hidden">
            {["en", "nl"].map((l) => (
              <button key={l} type="button" onClick={() => setReportLang(l)} data-testid={`report-lang-${l}`}
                className={`px-3 py-1.5 text-xs font-bold transition-colors ${reportLang === l ? "bg-[#F26722] text-white" : "text-zinc-400 hover:text-white"}`}>
                {l.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        <Eyebrow>{t("Customer & Call")}</Eyebrow>
        <div className="space-y-3">
          <input className={inputCls} value={f.customer_name} onChange={set("customer_name")} placeholder={t("Customer name")} data-testid="edit-customer-input" />
          <input className={inputCls} value={f.address} onChange={set("address")} placeholder={t("Address")} />
          <input className={inputCls} value={f.contact_person} onChange={set("contact_person")} placeholder={t("Contact person")} />
          <input className={inputCls} value={f.call_id} onChange={set("call_id")} placeholder={t("Call ID")} />
          <input className={inputCls} type="date" value={f.report_date} onChange={set("report_date")} />
        </div>

        <Eyebrow>{t("Machines")} ({machines.length})</Eyebrow>
        {machines.map((m, i) => (<MachineCard key={i} m={m} idx={i} catalog={catalog} machineTypes={machineTypes} checklistTpl={typeMap[m.machine_type_id]?.checklist} onChecklist={(v) => setMachine(i, "checklist", v)} onType={(v) => setMachine(i, "machine_type_id", v)} onSerial={(v) => setMachine(i, "serial_number", v)} onAdd={(p) => addPart(i, p)} onRemove={(pi) => removePart(i, pi)} />))}
        <button onClick={() => setMachines([...machines, { serial_number: "", parts: [] }])} className="w-full border border-dashed border-zinc-700 text-[#F26722] rounded-xl py-3 mt-2 font-semibold flex items-center justify-center gap-2"><Plus size={18} /> {t("Add another machine")}</button>

        <Eyebrow>{t("Hours")}</Eyebrow>
        {hours.map((h, i) => (
          <div key={i} className="grid grid-cols-4 gap-2 mb-2">
            <input className={inputCls} type="date" value={h.date || ""} onChange={(e) => setHours(hours.map((x, j) => j === i ? { ...x, date: e.target.value } : x))} />
            <input className={inputCls} type="number" placeholder="work h" value={h.work_hours || ""} onChange={(e) => setHours(hours.map((x, j) => j === i ? { ...x, work_hours: e.target.value } : x))} />
            <input className={inputCls} type="number" placeholder="travel h" value={h.travel_hours || ""} onChange={(e) => setHours(hours.map((x, j) => j === i ? { ...x, travel_hours: e.target.value } : x))} />
            <div className="flex gap-1"><input className={inputCls} placeholder="desc" value={h.description || ""} onChange={(e) => setHours(hours.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} /><button onClick={() => setHours(hours.filter((_, j) => j !== i))} className="text-zinc-500"><Trash2 size={16} /></button></div>
          </div>
        ))}
        <button onClick={() => setHours([...hours, { date: today, work_hours: "", travel_hours: "", description: "" }])} className="w-full border border-dashed border-zinc-700 text-[#F26722] rounded-xl py-3 font-semibold flex items-center justify-center gap-2"><Clock size={18} /> {t("Add hours entry")}</button>

        <Eyebrow>{t("Trips")}</Eyebrow>
        {trips.map((t, i) => (
          <div key={i} className="grid grid-cols-4 gap-2 mb-2">
            <input className={inputCls} placeholder="From" value={t.from_location || ""} onChange={(e) => setTrips(trips.map((x, j) => j === i ? { ...x, from_location: e.target.value } : x))} />
            <input className={inputCls} placeholder="To" value={t.to_location || ""} onChange={(e) => setTrips(trips.map((x, j) => j === i ? { ...x, to_location: e.target.value } : x))} />
            <input className={inputCls} type="number" placeholder="km" value={t.distance_km || ""} onChange={(e) => setTrips(trips.map((x, j) => j === i ? { ...x, distance_km: e.target.value } : x))} />
            <div className="flex gap-1"><input className={inputCls} placeholder="time" value={t.travel_time || ""} onChange={(e) => setTrips(trips.map((x, j) => j === i ? { ...x, travel_time: e.target.value } : x))} /><button onClick={() => setTrips(trips.filter((_, j) => j !== i))} className="text-zinc-500"><Trash2 size={16} /></button></div>
          </div>
        ))}
        <button onClick={() => setTrips([...trips, { date: today, from_location: "", to_location: "", distance_km: "", travel_time: "" }])} className="w-full border border-dashed border-zinc-700 text-[#F26722] rounded-xl py-3 font-semibold flex items-center justify-center gap-2"><Route size={18} /> {t("Add trip")}</button>

        <Eyebrow>{t("Work Performed")}</Eyebrow>
        <textarea className={inputCls + " min-h-32"} value={f.work_report} onChange={set("work_report")} placeholder={t("Describe what was done...")} data-testid="edit-work-input" />

        <Eyebrow>{t("Customer Signature")}</Eyebrow>
        <div className="mb-3"><label className="text-xs text-zinc-400">{t("Customer name")}</label><input data-testid="edit-signature-name-input" className={inputCls} value={sigName} onChange={(e) => setSigName(e.target.value)} placeholder={t("Name of person signing")} /></div>
        <SignaturePad onChange={setSig} initial={sig} />

        <Eyebrow>{t("Office Recipient")}</Eyebrow>
        <div className="space-y-2">
          {recipients.map((o) => (
            <button key={o.id} onClick={() => setF({ ...f, recipient_id: o.id })} className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all ${f.recipient_id === o.id ? "border-[#F26722] bg-[#3B1D0E]/40" : "border-zinc-800 bg-[#18181B]"}`}>
              <div className="text-left"><p className="font-bold text-white">{o.name}</p><p className="text-xs text-zinc-500">{o.email}</p></div>
              <div className={`w-5 h-5 rounded-full border-2 ${f.recipient_id === o.id ? "border-[#F26722] bg-[#F26722]" : "border-zinc-600"}`} />
            </button>
          ))}
        </div>

        <button data-testid="save-report-btn" onClick={save} disabled={loading} className="w-full bg-[#F26722] hover:bg-[#D95513] disabled:opacity-60 text-white font-bold py-4 rounded-xl mt-6 flex items-center justify-center gap-2">
          {loading ? <Loader2 className="animate-spin" size={20} /> : <><Save size={18} /> {t("Save Changes")}</>}
        </button>
      </div>
    </div>
  );
}
